import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, SignOut } from '@phosphor-icons/react';
import { useAuthStore } from '../../stores/authStore';
import ProfileDialog from '../profile/ProfilePage';

function initials(name: string, email: string) {
  const src = name || email;
  const parts = src.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

interface UserMenuProps {
  size?: number; // avatar circle size in px
}

export default function UserMenu({ size = 30 }: UserMenuProps) {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  function handleLogout() {
    logout();
    navigate('/login');
  }

  const label = user ? initials(user.name ?? '', user.email) : 'U';
  const avatarBg = user?.avatarUrl ? undefined : 'bg-gradient-to-br from-accent to-violet';

  return (
    <>
      <div ref={ref} className="relative shrink-0">
        {/* Avatar button */}
        <button
          onClick={() => setOpen((o) => !o)}
          title="Account"
          style={{ width: size, height: size }}
          className={`rounded-full flex items-center justify-center text-white text-xs font-sans font-medium shrink-0 ring-2 ring-transparent hover:ring-accent/50 transition-all duration-fast overflow-hidden ${avatarBg ?? ''}`}
        >
          {user?.avatarUrl
            ? <img src={user.avatarUrl} alt={user.name} className="w-full h-full object-cover" />
            : label
          }
        </button>

        {/* Dropdown */}
        {open && (
          <div className="absolute top-full right-0 mt-2 z-50 bg-bg-elevated border border-border rounded-xl shadow-elevated w-[220px] py-1 overflow-hidden">
            {/* User info header */}
            <div className="px-4 py-3 border-b border-border-subtle">
              <div className="flex items-center gap-3">
                <div
                  style={{ width: 36, height: 36 }}
                  className={`rounded-full flex items-center justify-center text-white text-sm font-medium shrink-0 overflow-hidden ${avatarBg ?? ''}`}
                >
                  {user?.avatarUrl
                    ? <img src={user.avatarUrl} alt={user.name} className="w-full h-full object-cover" />
                    : label
                  }
                </div>
                <div className="min-w-0">
                  <p className="font-sans text-sm font-medium text-text-primary truncate">{user?.name || '—'}</p>
                  <p className="font-sans text-[11px] text-text-tertiary truncate">{user?.email}</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="py-1">
              <button
                onClick={() => { setOpen(false); setShowProfile(true); }}
                className="w-full flex items-center gap-2.5 px-4 py-2 text-sm font-sans text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-colors duration-fast"
              >
                <User size={14} />
                Profile
              </button>
              <div className="border-t border-border-subtle my-1" />
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2.5 px-4 py-2 text-sm font-sans text-error hover:text-error hover:bg-error/10 transition-colors duration-fast"
              >
                <SignOut size={14} />
                Sign out
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Profile dialog (rendered outside the dropdown so it's full-screen) */}
      {showProfile && <ProfileDialog onClose={() => setShowProfile(false)} />}
    </>
  );
}
